import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, ExternalLink } from "lucide-react";
import api from "../../lib/api";

const APP_STATUSES = ["New", "Reviewed", "Shortlisted", "Rejected", "Hired"];

export default function AdminApplications() {
  const [apps, setApps] = useState([]);

  const load = () => api.get("/admin/applications").then((r) => setApps(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/applications/${id}`, { status });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success(`Application marked "${status}"`);
    } catch {
      toast.error("Could not update");
    }
  };

  const downloadResume = async (app) => {
    try {
      const res = await api.get(`/admin/applications/${app.id}/resume`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = app.resume_filename || "resume.pdf";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Resume not available");
    }
  };

  return (
    <div data-testid="admin-applications-page">
      <h1 className="font-display font-extrabold text-2xl text-[#F9FAFB]">Career Applications</h1>
      <p className="text-sm text-[#9CA3AF] mt-1">{apps.length} applications received.</p>

      <div className="mt-6 bg-[#111827] border border-[#1F2937] rounded-xl overflow-hidden">
        {apps.length === 0 ? (
          <p className="px-5 py-10 text-sm text-[#6B7280]" data-testid="applications-empty">No applications yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280] border-b border-[#1F2937]">
                  <th className="px-5 py-3">Candidate</th>
                  <th className="px-5 py-3">Position</th>
                  <th className="px-5 py-3">Experience</th>
                  <th className="px-5 py-3">Education</th>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Resume</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={a.id} className="border-b border-[#1F2937]/50 last:border-0 hover:bg-[#0B0F19]/40" data-testid={`application-row-${a.id.slice(0, 8)}`}>
                    <td className="px-5 py-3.5">
                      <p className="font-semibold text-[#F9FAFB] flex items-center gap-2">
                        {a.full_name}
                        {a.linkedin && (
                          <a href={a.linkedin} target="_blank" rel="noreferrer" className="text-[#10B981]" title="LinkedIn">
                            <ExternalLink size={12} />
                          </a>
                        )}
                      </p>
                      <p className="text-xs text-[#6B7280]">{a.mobile}{a.email ? ` · ${a.email}` : ""}</p>
                      {a.cover_note && <p className="text-xs text-[#6B7280] mt-1 max-w-[240px] truncate" title={a.cover_note}>{a.cover_note}</p>}
                    </td>
                    <td className="px-5 py-3.5 text-[#9CA3AF]">{a.position || "—"}</td>
                    <td className="px-5 py-3.5 text-[#9CA3AF]">{a.experience || "—"}</td>
                    <td className="px-5 py-3.5 text-[#9CA3AF]">{a.education || "—"}</td>
                    <td className="px-5 py-3.5 text-[#9CA3AF] text-xs whitespace-nowrap">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}
                    </td>
                    <td className="px-5 py-3.5">
                      <select
                        value={a.status}
                        onChange={(e) => updateStatus(a.id, e.target.value)}
                        data-testid={`application-status-${a.id.slice(0, 8)}`}
                        className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold border border-[#1F2937] bg-[#0B0F19] text-[#F9FAFB] focus:outline-none cursor-pointer"
                      >
                        {APP_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      {a.resume_path ? (
                        <button
                          onClick={() => downloadResume(a)}
                          data-testid={`application-resume-${a.id.slice(0, 8)}`}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#10B981]/15 text-[#10B981] text-xs font-bold hover:bg-[#10B981]/25 transition-colors"
                        >
                          <Download size={13} /> {a.resume_filename || "Resume"}
                        </button>
                      ) : (
                        <span className="text-xs text-[#6B7280]">No resume</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
